import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import BackButton from "./back-button";
import {
  ComparisonFilterValue,
  ComparisonOperator,
  DateMenuItem,
  NumericMenuItem,
} from "./types";

export default function ComparisonPanel({
  menuItem,
  inputType,
  operatorKeys,
  value,
  onChange,
  onBack,
}: {
  menuItem: DateMenuItem | NumericMenuItem;
  inputType: "date" | "number";
  operatorKeys: Record<ComparisonOperator, string>;
  value?: ComparisonFilterValue;
  onChange: (value: ComparisonFilterValue) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [localOperator, setLocalOperator] = useState<ComparisonOperator>(
    value?.operator ?? "eq",
  );
  const [localValue, setLocalValue] = useState(value?.value ?? "");

  function selectOperator(operator: ComparisonOperator) {
    setLocalOperator(operator);
    if (localValue) {
      onChange({ operator, value: localValue });
    }
  }

  function handleValueChange(newValue: string) {
    setLocalValue(newValue);
    if (newValue) {
      onChange({ operator: localOperator, value: newValue });
    }
  }

  return (
    <div>
      <BackButton label={menuItem.label} onBack={onBack} />
      {(Object.entries(operatorKeys) as [ComparisonOperator, string][]).map(
        ([operator, key]) => {
          const isSelected = localOperator === operator;
          return (
            <DropdownMenu.Item
              key={operator}
              onSelect={(e) => {
                e.preventDefault();
                selectOperator(operator);
              }}
              className="flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2 text-sm text-default outline-none hover:bg-tint focus:bg-tint focus:ring-2 focus:ring-focus"
            >
              <div
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${isSelected ? "border-primary" : "border-default"}`}
              >
                {isSelected && (
                  <div className="h-2 w-2 rounded-full bg-primary" />
                )}
              </div>
              <span className="flex-1">{t(key)}</span>
            </DropdownMenu.Item>
          );
        },
      )}
      <DropdownMenu.Separator className="my-1 h-px bg-raised" />
      <div className="px-3 py-2">
        <input
          aria-label={t("filterValue")}
          type={inputType}
          value={localValue}
          onChange={(e) => handleValueChange(e.target.value)}
          className="w-full rounded border border-default px-2 py-1.5 text-sm outline-none focus:border-focus focus:ring-1 focus:ring-focus"
        />
      </div>
    </div>
  );
}
